/**
 * Custom Google OAuth button — wraps `useGoogleLogin` from
 * `@react-oauth/google` in our gold-on-white luxury button shell.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. The Google JS SDK uses the OAuth client ID from
 * the `GoogleOAuthProvider` ancestor (wired in App.js from
 * REACT_APP_GOOGLE_CLIENT_ID env var). The popup flow does not need a
 * redirect URI at all — Google's JS SDK uses postMessage.
 *
 * Flow:
 *   1. User clicks → Google popup opens (Implicit ID-token flow via
 *      `useGoogleLogin({ flow: 'implicit', scope: 'openid email profile' })`).
 *   2. Success handler receives `access_token` → fetch userinfo from
 *      Google → POST email+name+picture to our backend? NO. Safer:
 *      switch to `<GoogleLogin>` component which emits a signed ID
 *      token (JWT) that the backend verifies.
 *
 * We use the `<GoogleLogin>` component for that reason.
 */
import { GoogleLogin } from "@react-oauth/google";
import axios, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/apiError";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { getStoredReferralCode, clearReferralCookie } from "@/lib/vendorReferral";
import { trackEvent } from "@/lib/analytics";

export default function GoogleAuthButton({ next = "/app", label = "Continue with Google" }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(false);

  const onCredential = async (credentialResponse) => {
    const credential = credentialResponse?.credential;
    if (!credential) {
      toast.error("Google sign-in didn't return a credential.");
      return;
    }
    setVerifying(true);
    try {
      const ref = getStoredReferralCode() || undefined;
      const res = await axios.post(
        `${API}/auth/google`,
        { credential, ref },
        { withCredentials: true },
      );
      if (ref) clearReferralCookie();
      setUser(res.data);
      // Google flow can be either sign-in or first-time signup; only
      // the first-time case counts as top-of-funnel for GA4 conversion.
      if (res.data?.new_user) {
        trackEvent("signup_started", { method: "google" });
      }
      toast.success(
        res.data?.new_user
          ? `Welcome to Facetra, ${res.data.name}.`
          : `Signed in as ${res.data.name}.`,
      );
      // Use react-router navigate so we stay within the SPA and the
      // back-button never lands on a stale OAuth URL (the popup flow
      // doesn't add any external history entries either way).
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "Google sign-in failed."));
      setVerifying(false);
    }
  };

  return (
    <div className="relative mt-8 w-full" data-testid="google-auth-wrap">
      {verifying && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/55 rounded-full text-white text-xs gap-2 backdrop-blur-sm">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-[#D4AF37]" /> Signing in…
        </div>
      )}
      {/* Google's official button — themed to match our white card style.
          We wrap it in a fixed-height container so the layout doesn't
          jump while Google's iframe loads. */}
      <div className="flex justify-center" style={{ minHeight: 44 }}>
        <GoogleLogin
          onSuccess={onCredential}
          onError={() => toast.error("Google sign-in failed. Try again.")}
          theme="filled_black"
          shape="pill"
          size="large"
          text={label === "Sign up with Google" ? "signup_with" : "continue_with"}
          width="320"
          data-testid="signin-google-btn"
        />
      </div>
    </div>
  );
}
